"use client"

import { cn } from "@/lib/utils"
import { surface } from "@/lib/dashboard-ui"
import { Skeleton } from "@/components/ui/skeleton"
import { Target, TrendingUp, Presentation, BookOpen, Info } from "lucide-react"
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { focusRing } from "@/lib/dashboard-ui"

interface ProjectedScoreCardProps {
    totalScore: number
    mathScore: number
    readingWritingScore: number
    /** The student's own target from their study plan; falls back to a perfect score. */
    targetScore?: number
    loading?: boolean
    className?: string
    isDeveloper?: boolean
    /** Opens the score-breakdown dialog. Omit to hide the trigger. */
    onShowBreakdown?: () => void
}

const MAX_SCORE = 1600
const MIN_SCORE = 400

function SectionBar({
    icon: Icon,
    label,
    score,
    max,
    barClass,
}: {
    icon: typeof Presentation
    label: string
    score: number
    max: number
    barClass: string
}) {
    const pct = Math.min((score / max) * 100, 100)
    return (
        <div>
            <div className="mb-2 flex items-center justify-between gap-3">
                <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    {label}
                </span>
                <span className="flex items-baseline gap-0.5 tabular-nums">
                    <span className="text-xl font-bold tracking-tight text-foreground">{score || 0}</span>
                    <span className="text-sm font-medium text-muted-foreground">/{max}</span>
                </span>
            </div>
            <div
                role="progressbar"
                aria-valuenow={score || 0}
                aria-valuemin={0}
                aria-valuemax={max}
                aria-label={`${label} projected score: ${score || 0} out of ${max}`}
                className="h-2 w-full overflow-hidden rounded-full bg-muted"
            >
                <div
                    className={cn("h-full rounded-full motion-safe:transition-[width] motion-safe:duration-700", barClass)}
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    )
}

export function ProjectedScoreCard({
    totalScore = 0,
    mathScore = 0,
    readingWritingScore = 0,
    targetScore = MAX_SCORE,
    loading = false,
    className,
    isDeveloper = false,
    onShowBreakdown,
}: ProjectedScoreCardProps) {
    if (loading) {
        return (
            <div className={cn(surface, "overflow-hidden", className)}>
                <div className="flex flex-col md:flex-row">
                    <div className="flex flex-1 flex-col justify-center border-b border-border p-6 md:border-b-0 md:border-r md:p-8">
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="mt-4 h-16 w-40" />
                        <Skeleton className="mt-4 h-2 w-full" />
                        <Skeleton className="mt-4 h-8 w-44 rounded-full" />
                    </div>
                    <div className="flex flex-1 flex-col justify-center gap-7 bg-muted/20 p-6 md:p-8">
                        {[0, 1].map((i) => (
                            <div key={i}>
                                <Skeleton className="mb-2 h-4 w-28" />
                                <Skeleton className="h-2 w-full" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )
    }

    const maxSection = Math.round(MAX_SCORE / 2)
    const gap = targetScore - totalScore
    // Meter runs 400–1600 (the real SAT floor), not 0–1600, so the bar reflects
    // where the score actually sits on the scale.
    const meterPct = Math.min(
        Math.max(((totalScore - MIN_SCORE) / (MAX_SCORE - MIN_SCORE)) * 100, 0),
        100
    )

    return (
        <div className={cn(surface, "overflow-hidden", className)}>
            <div className="flex flex-col md:flex-row">
                {/* Total */}
                <div className="flex flex-1 flex-col justify-center border-b border-border p-6 md:border-b-0 md:border-r md:p-8">
                    <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                        <h2 className="text-sm font-semibold text-muted-foreground">Projected SAT score</h2>
                        {isDeveloper ? (
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button
                                        type="button"
                                        aria-label="Show the score algorithm (developer view)"
                                        className={cn(
                                            // grid-2.2 target size: the 16px glyph needs padding to clear 24x24.
                                            "-m-1 grid h-6 w-6 place-items-center rounded text-muted-foreground/60 transition-colors hover:text-foreground",
                                            focusRing
                                        )}
                                    >
                                        <Info className="h-4 w-4" aria-hidden="true" />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent side="bottom" align="start" className="w-[380px] p-4 font-mono text-xs">
                                    <p className="mb-3 text-[11px] font-bold uppercase tracking-widest text-primary">
                                        Score algorithm — developer view
                                    </p>
                                    <div className="space-y-3 leading-relaxed text-muted-foreground">
                                        <div>
                                            <p className="mb-1 font-semibold text-foreground">1. Categorize attempts</p>
                                            <p className="rounded bg-muted px-2 py-1">{`if "math" in question_category → Math`}</p>
                                            <p className="mt-1 rounded bg-muted px-2 py-1">{`else → Reading & Writing`}</p>
                                        </div>
                                        <div>
                                            <p className="mb-1 font-semibold text-foreground">2. Per-section score (200–800)</p>
                                            <div className="space-y-0.5 rounded bg-muted px-2 py-1.5">
                                                <p>{`if attempts < 5  →  return 200`}</p>
                                                <p>{`accuracy = correct / attempts`}</p>
                                                <p>{`acc_score = accuracy × 600`}</p>
                                                <p>{`bonus    = min(50, attempts ÷ 2)`}</p>
                                                <p>{`raw      = 200 + acc_score + bonus`}</p>
                                                <p>{`section  = clamp(round10(raw), 200, 800)`}</p>
                                            </div>
                                        </div>
                                        <div>
                                            <p className="mb-1 font-semibold text-foreground">3. Total</p>
                                            <p className="rounded bg-muted px-2 py-1">{`total = math_score + rw_score  (400–1600)`}</p>
                                        </div>
                                        <div>
                                            <p className="mb-1 font-semibold text-foreground">4. Cache</p>
                                            <p className="rounded bg-muted px-2 py-1">{`Redis  key=projected_score_v3:{user_id}  TTL=24h`}</p>
                                        </div>
                                    </div>
                                </PopoverContent>
                            </Popover>
                        ) : (
                            <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button
                                            type="button"
                                            aria-label="How this score is calculated"
                                            className={cn(
                                            // grid-2.2 target size: the 16px glyph needs padding to clear 24x24.
                                            "-m-1 grid h-6 w-6 place-items-center rounded text-muted-foreground/60 transition-colors hover:text-foreground",
                                            focusRing
                                        )}
                                        >
                                            <Info className="h-4 w-4" aria-hidden="true" />
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" align="start" className="max-w-[260px] text-xs leading-relaxed">
                                        <p className="mb-1 font-semibold">How is this calculated?</p>
                                        <p>
                                            Based on your accuracy across every practice question you have answered. Each
                                            section is scored out of 800 using performance-weighted scaling, then combined
                                            for a total out of 1600.
                                        </p>
                                        <p className="mt-1 opacity-80">Answer more questions for a more accurate estimate.</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                    </div>

                    <p className="mt-3 flex items-baseline gap-2">
                        <span className="text-6xl font-bold leading-none tracking-tight text-foreground tabular-nums md:text-7xl">
                            {totalScore}
                        </span>
                        <span className="text-xl font-semibold text-muted-foreground">/ {MAX_SCORE}</span>
                    </p>

                    <div
                        role="progressbar"
                        aria-valuenow={totalScore}
                        aria-valuemin={MIN_SCORE}
                        aria-valuemax={MAX_SCORE}
                        aria-label={`Projected total score: ${totalScore} out of ${MAX_SCORE}`}
                        className="relative mt-5 h-2 w-full overflow-hidden rounded-full bg-muted"
                    >
                        <div
                            className="h-full rounded-full bg-primary motion-safe:transition-[width] motion-safe:duration-700"
                            style={{ width: `${meterPct}%` }}
                        />
                    </div>

                    {gap > 0 && (
                        <p className="mt-4 inline-flex w-fit items-center gap-2 rounded-full border border-border bg-muted/50 px-3 py-1.5">
                            <TrendingUp className="h-4 w-4 text-primary" aria-hidden="true" />
                            <span className="text-xs font-semibold text-muted-foreground">
                                <span className="text-foreground tabular-nums">{gap}</span> points to your{" "}
                                <span className="tabular-nums">{targetScore}</span> target
                            </span>
                        </p>
                    )}
                    {gap <= 0 && totalScore > 0 && (
                        <p className="mt-4 inline-flex w-fit items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5">
                            <TrendingUp className="h-4 w-4 text-primary" aria-hidden="true" />
                            <span className="text-xs font-semibold text-foreground">
                                Target of {targetScore} reached
                            </span>
                        </p>
                    )}

                    {onShowBreakdown && (
                        <button
                            type="button"
                            onClick={onShowBreakdown}
                            className={cn(
                                "mt-4 flex h-6 w-fit items-center rounded text-sm font-semibold text-primary underline-offset-4 hover:underline",
                                focusRing
                            )}
                        >
                            See how this is scored
                        </button>
                    )}
                </div>

                {/* Sections */}
                <div className="flex flex-1 flex-col justify-center gap-7 bg-muted/20 p-6 md:p-8">
                    <SectionBar
                        icon={Presentation}
                        label="Math"
                        score={mathScore}
                        max={maxSection}
                        barClass="bg-primary"
                    />
                    {/* chart-3, not chart-2: chart-2 is the yellow brand accent in
                        `dsat-school`, where it measures 1.38:1 against the muted track —
                        the bar was effectively invisible. chart-3 clears the 3:1 non-text
                        minimum in all six themes (3.30 worst case, in `custom`). */}
                    <SectionBar
                        icon={BookOpen}
                        label="Reading & Writing"
                        score={readingWritingScore}
                        max={maxSection}
                        barClass="bg-[var(--chart-3)]"
                    />
                </div>
            </div>
        </div>
    )
}
